import {
	InfoText,
	Label,
	NewOrderDetailsWrapper,
	StaticComponentBackground,
	StaticComponentGrid,
	StaticComponentGridDividerWrapper,
	StaticComponentTitle
} from "../NewOrders.styles";
import { Divider } from "@mui/material";
import {
	NewOrderAddressTitle,
	NewOrderDeliveryContact,
	NewOrderOrderDetailsTitle,
	NewOrderOrderPurposeTitle,
	NewOrderRequestedDeliveryDate,
	NewOrderReviewDesignerLabel,
	NewOrderReviewJobNameLabel,
	NewOrderReviewPOLabel,
	NewOrderShippingLabelNotes,
	NewOrderShippingMethod,
	NewOrderShipToTitle,
	NewOrderUnavailableDataPlaceholderText
} from "../constants";
import { useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import { EditText, UnavailableDataPlaceholderText } from "constants/text";
import { LinkButton } from "components/Common/Link";
import { useNavigate, useParams } from "react-router";
import { useMemo } from "react";
import {
	businessPurposeEnumDescription,
	getConfigurationAccountInfo
} from "components/NewOrders/utils/NewOrderUtils.tsx";
import { PageProps } from "pages/OrderPages/BuildOrderPage/BuildOrderPage";
import { getNewOrderURL } from "utils/string.ts";
import { useUserLocation } from "hooks/useUserLocation.ts";
import { formatAddress } from "utils/address.ts";

const ReviewOrderDetails = ({ isAddOn, isNewQuoteOrder }: PageProps) => {
	const navigate = useNavigate();
	const params = useParams();
	const { isCustomColorPage: isCustomColor, isConfirmationPage } = useUserLocation();

	const handleEdit = () => {
		navigate(
			getNewOrderURL(
				isAddOn,
				isCustomColor,
				isNewQuoteOrder,
				isNewQuoteOrder ? "review-order" : "shipping-details",
				params.orderId
			)
		);
	};

	const newOrderDetails = useSelector((state: RootState) => state.newOrder.newOrderDetails);
	const productLineAccountInfo = useSelector((state: RootState) => state.newOrder.productLineAccounts);

	const allowLabelComments = useMemo(() => {
		return newOrderDetails?.configurations?.map(
			(config) => getConfigurationAccountInfo(config, productLineAccountInfo)?.allowLabelComments
		);
	}, [newOrderDetails?.configurations, productLineAccountInfo]);

	const confirmedDeliveryDate = newOrderDetails?.requestedDeliveryDate;
	const formattedDeliveryDate = `${confirmedDeliveryDate?.slice(5, 7)}/${confirmedDeliveryDate?.slice(8, 10)}/${confirmedDeliveryDate?.slice(0, 4)}`;

	if (isNewQuoteOrder) return <></>;

	return (
		<StaticComponentBackground>
			<NewOrderDetailsWrapper isConfirmation={isConfirmationPage}>
				<StaticComponentTitle>
					{NewOrderOrderDetailsTitle}
					{!isConfirmationPage && (
						<span>
							<LinkButton
								data-testid="new-order-review-edit"
								onClick={handleEdit}
							>
								{EditText}
							</LinkButton>
						</span>
					)}
				</StaticComponentTitle>
				<StaticComponentGrid>
					<Label>
						{NewOrderReviewDesignerLabel}
						<InfoText data-testid="new-order-review-designer-number">
							{newOrderDetails?.designerNumber && newOrderDetails?.designerNumber !== ""
								? newOrderDetails.designerNumber
								: UnavailableDataPlaceholderText}
						</InfoText>
					</Label>

					<Label>
						{NewOrderReviewPOLabel}
						<InfoText data-testid="new-order-review-po-number">
							{newOrderDetails?.poNumber
								? newOrderDetails.poNumber
								: NewOrderUnavailableDataPlaceholderText}
						</InfoText>
					</Label>

					<Label>
						{NewOrderReviewJobNameLabel}
						<InfoText data-testid="new-order-review-job-name">
							{newOrderDetails?.jobName
								? newOrderDetails.jobName
								: NewOrderUnavailableDataPlaceholderText}
						</InfoText>
					</Label>
					{newOrderDetails?.orderType === "ORIGINAL" && (
						<Label>
							{NewOrderOrderPurposeTitle}
							<InfoText data-testid="new-order-review-business-purpose">
								{newOrderDetails?.businessPurpose
									? businessPurposeEnumDescription(newOrderDetails.businessPurpose)
									: NewOrderUnavailableDataPlaceholderText}
							</InfoText>
						</Label>
					)}
				</StaticComponentGrid>

				<StaticComponentGridDividerWrapper>
					<Divider />
				</StaticComponentGridDividerWrapper>

				<StaticComponentTitle>{NewOrderShipToTitle}</StaticComponentTitle>
				<StaticComponentGrid>
					<Label>
						{NewOrderAddressTitle}
						<InfoText data-testid="new-order-review-address">
							<div>{formatAddress(newOrderDetails?.shipToAddress)}</div>
						</InfoText>
					</Label>

					<Label>
						{NewOrderRequestedDeliveryDate}
						<InfoText data-testid="new-order-review-requested-delivery-date">
							{newOrderDetails?.requestedDeliveryDate
								? formattedDeliveryDate
								: NewOrderUnavailableDataPlaceholderText}
						</InfoText>
					</Label>

					<Label>
						{NewOrderDeliveryContact}
						<InfoText data-testid="new-order-review-delivery-contact">
							{newOrderDetails?.shipToAddress?.phoneNumber ? (
								<div>{newOrderDetails.shipToAddress?.phoneNumber}</div>
							) : (
								<div>{NewOrderUnavailableDataPlaceholderText}</div>
							)}
							{newOrderDetails?.shipToAddress?.email}
						</InfoText>
					</Label>

					<Label>
						{NewOrderShippingMethod}
						<InfoText data-testid="new-order-review-shipping-method">
							{newOrderDetails?.isCustomerPickup ? "Pickup" : "Delivery"}
						</InfoText>
					</Label>

					{allowLabelComments?.includes(true) && !newOrderDetails?.isCustomerPickup && (
						<Label>
							{NewOrderShippingLabelNotes}
							<InfoText data-testid="new-order-review-shipping-notes">
								{newOrderDetails?.labelComments}
							</InfoText>
						</Label>
					)}
				</StaticComponentGrid>
			</NewOrderDetailsWrapper>
		</StaticComponentBackground>
	);
};

export default ReviewOrderDetails;
